import { Dispatch, FC, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { STRIPE_PRIMARY_PRODUCT_KINDS, StripeProductKindToPaymentMethodMap } from 'constants/payments'
import { useGetUpsellPaymentIntent, useUpdatePreferredPaymentMethod } from 'dataQueries'

import { InstructionOptionDTO } from 'models/purchaseFlow'
import { Nullable } from 'models/helpers'
import { PaymentIntentDTO } from 'models/stripe'
import { ProductKind } from 'constants/product'
import { SavedStripePaymentMethod } from 'models/user'
import { StripeError } from '@stripe/stripe-js'
import { UseMutationResult } from '@tanstack/react-query'
import { bigFromFee } from 'utils/price'
import { productKindIsPreferredPaymentMethod } from 'utils/typeguards'
import { useAuth0 } from 'utils/auth'
import { useGetUpsellBillingConfiguration } from 'dataQueries/purchase.query'
import { useSavedStripePaymentMethods } from 'components/contexts/SavedStripePaymentMethodsContext'
import { useUpsellPurchaseConfiguration } from './PurchaseConfiguration.context'
import { useUserData } from 'components/contexts/UserDataContext'

interface UpsellPaymentInterface {
  paymentIntent: Nullable<PaymentIntentDTO>
  isStripeUsed: boolean
  paymentProducts: InstructionOptionDTO[]
  selectedPaymentProduct?: InstructionOptionDTO
  isForeignPurchase: boolean
  isPaymentElementReady: boolean
  isPaymentElementValid: boolean
  stripeError: StripeError | null
  isStripeProcessing: boolean
  isStripeConfirmed: boolean
  updatePreferredPaymentMethod: UseMutationResult<any, any, any, any>
  receiptEmail: string
  isReceiptEmailValid: boolean
  isPurchaseReady: boolean
  saveStripePaymentMethod: boolean
  selectedSavedPaymentMethod: Nullable<SavedStripePaymentMethod>
  isPaymentLoading: boolean
  doStripeRedirect: boolean
  setStripeError: Dispatch<SetStateAction<StripeError | null>>
  setIsStripeProcessing: Dispatch<SetStateAction<boolean>>
  setIsStripeConfirmed: Dispatch<SetStateAction<boolean>>
  setIsPaymentElementReady: Dispatch<SetStateAction<boolean>>
  setIsPaymentElementValid: Dispatch<SetStateAction<boolean>>
  setReceiptEmail: Dispatch<SetStateAction<string>>
  setIsReceiptEmailValid: Dispatch<SetStateAction<boolean>>
  setSaveStripePaymentMethod: Dispatch<SetStateAction<boolean>>
  setSelectedPaymentProduct: Dispatch<SetStateAction<InstructionOptionDTO | undefined>>
  setSelectedSavedPaymentMethod: Dispatch<SetStateAction<Nullable<SavedStripePaymentMethod>>>
  setDoStripeRedirect: Dispatch<SetStateAction<boolean>>
}

const defaultUpsellPaymentValue: UpsellPaymentInterface = {
  paymentIntent: null,
  isStripeUsed: false,
  paymentProducts: [],
  selectedPaymentProduct: undefined,
  isForeignPurchase: false,
  isPaymentElementReady: false,
  isPaymentElementValid: false,
  stripeError: null,
  isStripeProcessing: false,
  isStripeConfirmed: false,
  isPurchaseReady: false,
  updatePreferredPaymentMethod: {} as UseMutationResult<any, any, any, any>,
  receiptEmail: '',
  isReceiptEmailValid: false,
  saveStripePaymentMethod: false,
  selectedSavedPaymentMethod: undefined,
  isPaymentLoading: true,
  doStripeRedirect: true,
  setStripeError: () => { throw new Error('setStripeError is not defined') },
  setIsStripeProcessing: () => { throw new Error('setIsStripeProcesing is not defined') },
  setIsStripeConfirmed: () => { throw new Error('setIsStripeConfirmed is not defined') },
  setIsPaymentElementReady: () => { throw new Error('setIsPaymentElementReady is not defined') },
  setIsPaymentElementValid: () => { throw new Error('setIsPaymentElementValid is not defined') },
  setSelectedPaymentProduct: () => { throw new Error('setSelectedPaymentProduct is not defined') },
  setReceiptEmail: () => { throw new Error('setReceiptEmail is not defined') },
  setIsReceiptEmailValid: () => { throw new Error('setIsReceiptEmailValid is not defined') },
  setSaveStripePaymentMethod: () => { throw new Error('setSaveStripePaymentMethod is not defined') },
  setSelectedSavedPaymentMethod: () => { throw new Error('setSelectedSavedPaymentMethod is not defined') },
  setDoStripeRedirect: () => { throw new Error('setDoStripeRedirect is not defined') },
}

/** Upsell payment context */
export const UpsellPaymentContext = createContext<UpsellPaymentInterface>(defaultUpsellPaymentValue)
/** Upsell payment context hook */
export const useUpsellPayment = (): UpsellPaymentInterface => useContext(UpsellPaymentContext)

/** Context provider for upsell payment */
export const UpsellPaymentContextProvider: FC<{
  children?: ReactNode
}> = ({
  children
}) => {

    const { roles } = useAuth0()

    const {
      isOpen,
      productId,
      productQuantity,
      assignmentId,
    } = useUpsellPurchaseConfiguration()

    const {
      fetchSavedStripePaymentMethods,
      savedStripePaymentMethodsMap,
      stripePaymentMethods
    } = useSavedStripePaymentMethods()

    const {
      clientData,
      userCanPayByInvoice,
      userPropertyWorkspaceId
    } = useUserData()


    // Saving payment methods
    const [saveStripePaymentMethod, setSaveStripePaymentMethod] = useState<boolean>(defaultUpsellPaymentValue.saveStripePaymentMethod)
    const [selectedSavedPaymentMethod, setSelectedSavedPaymentMethod] = useState<Nullable<SavedStripePaymentMethod>>(defaultUpsellPaymentValue.selectedSavedPaymentMethod)
    const [doStripeRedirect, setDoStripeRedirect] = useState<boolean>(true)

    // Preferred payment method
    const updatePreferredPaymentMethod = useUpdatePreferredPaymentMethod()
    const preferredPaymentMethod = useMemo(() => clientData?.preferredPaymentMethod || undefined, [clientData])

    // Payment intent
    const upsellPaymentIntentMutation = useGetUpsellPaymentIntent()
    const paymentIntent = useMemo(() => upsellPaymentIntentMutation.data?.data ?? null, [upsellPaymentIntentMutation.data]) as Nullable<PaymentIntentDTO>

    // Payment kinds
    const upsellBillingConfiguration = useGetUpsellBillingConfiguration(assignmentId, isOpen)
    const upsellBillingConfigurationData = useMemo(() => upsellBillingConfiguration.data?.data, [upsellBillingConfiguration])
    const paymentProducts = useMemo(() => upsellBillingConfigurationData?.paymentMethodList || [], [upsellBillingConfigurationData])
    const [selectedPaymentProduct, setSelectedPaymentProduct] = useState<InstructionOptionDTO>()
    const selectedStripePaymentMethod = useMemo(() => !!selectedPaymentProduct ? StripeProductKindToPaymentMethodMap.get(selectedPaymentProduct.kind) : undefined, [selectedPaymentProduct])

    // Whether it is foreign purchase (true = the purchaser is not the owner of the assignment)
    const isForeignPurchase = useMemo(() => upsellBillingConfigurationData?.foreignPurchase || false, [upsellBillingConfigurationData])

    // Payment element
    const [isPaymentElementReady, setIsPaymentElementReady] = useState<boolean>(defaultUpsellPaymentValue.isPaymentElementReady)
    const [isPaymentElementValid, setIsPaymentElementValid] = useState<boolean>(defaultUpsellPaymentValue.isPaymentElementValid)

    // Stripe status
    const [isStripeProcessing, setIsStripeProcessing] = useState<boolean>(defaultUpsellPaymentValue.isStripeProcessing)
    const [isStripeConfirmed, setIsStripeConfirmed] = useState<boolean>(defaultUpsellPaymentValue.isStripeConfirmed)
    const [stripeError, setStripeError] = useState<StripeError | null>(defaultUpsellPaymentValue.stripeError)

    // Receipt email
    const [receiptEmail, setReceiptEmail] = useState<string>(defaultUpsellPaymentValue.receiptEmail)
    const [isReceiptEmailValid, setIsReceiptEmailValid] = useState<boolean>(defaultUpsellPaymentValue.isReceiptEmailValid)

    // Meta
    const isStripeUsed = useMemo(() => !!selectedPaymentProduct && STRIPE_PRIMARY_PRODUCT_KINDS.has(selectedPaymentProduct.kind), [selectedPaymentProduct])
    const isPurchaseReady = useMemo(() => {
      if (!upsellBillingConfiguration.isSuccess) return false
      if (!roles.isAdmin && !stripePaymentMethods?.isSuccess) return false
      return true
    }, [upsellBillingConfiguration.isSuccess, roles.isAdmin, stripePaymentMethods?.isSuccess])

    const isPaymentLoading = useMemo(() => {
      if (selectedSavedPaymentMethod === null && !isPaymentElementReady) return true
      return false
    }, [isPaymentElementReady, selectedSavedPaymentMethod])

    // Fetch billing configuration and saved methods when modal opens
    useEffect(() => {
      if (isOpen) {
        // Prevent backdrop stutter
        window.setTimeout(() => {
          if (assignmentId && !roles.isAdmin) fetchSavedStripePaymentMethods()
        }, 300)
      }
    }, [assignmentId, fetchSavedStripePaymentMethods, isOpen, roles.isAdmin])

    // Preselect either default or the only available method
    const handlePrefill = useCallback(() => {

      // Ignore if there is already selected payment method
      if (selectedPaymentProduct) return

      // If there is only 1 option, preselect it
      if (paymentProducts.length === 1) {
        // Check that the only option is not invoice and user is created after public launch with no subscriptions and invoice product is without surcharge
        if (paymentProducts[0].kind === ProductKind.INVOICE_BY_EMAIL && !userCanPayByInvoice && bigFromFee(paymentProducts[0].feePrice).eq(0)) return
        setSelectedPaymentProduct(paymentProducts[0])
        return
      }

      // Admins have no preferred method selection option
      if (!roles.isClient) return

      // Check that preferred payment method is in available options
      const preferredProduct = paymentProducts.find((product) => product.kind === preferredPaymentMethod)

      // If preferred method is not in options do not select it
      if (!preferredPaymentMethod || !preferredProduct) return

      // Check that preferred payment is invoice and user is created after public launch with no subscriptions
      if (preferredProduct.kind === ProductKind.INVOICE_BY_EMAIL && !userCanPayByInvoice) return

      setSelectedPaymentProduct(preferredProduct)
    }, [selectedPaymentProduct, paymentProducts, roles.isClient, preferredPaymentMethod, userCanPayByInvoice])

    // Trigger the preselect
    useEffect(handlePrefill, [handlePrefill])

    // Reset paymentIntent and selectedSavedPaymentMethod when selected payment kind changes
    useEffect(() => {
      upsellPaymentIntentMutation.reset()
      setSelectedSavedPaymentMethod(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId, selectedPaymentProduct])

    // Get payment intent when all data are set and stripe payment method is selected
    useEffect(() => {
      if (!isStripeUsed) return
      if (!assignmentId) return
      if (!productId) return
      if (!productQuantity) return
      if (!selectedStripePaymentMethod) return

      upsellPaymentIntentMutation.mutate({
        assignmentId,
        workspaceId: userPropertyWorkspaceId,
        productId,
        quantity: productQuantity,
        paymentMethod: selectedStripePaymentMethod,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStripeUsed, assignmentId, productId, productQuantity, selectedPaymentProduct, selectedStripePaymentMethod, userPropertyWorkspaceId])

    // Reset readiness and validity of payment element when method changes to saved/undefined
    useEffect(() => {
      if (selectedSavedPaymentMethod !== null) {
        if (isPaymentElementValid) setIsPaymentElementValid(false)
        if (isPaymentElementReady) setIsPaymentElementReady(false)
      }
    }, [isPaymentElementReady, isPaymentElementValid, selectedSavedPaymentMethod, setIsPaymentElementReady, setIsPaymentElementValid])

    // If no saved methods for selected payment, push null to selectedSavedMethod
    useEffect(() => {
      if (!stripePaymentMethods?.isSuccess) return
      if (!selectedPaymentProduct || !productKindIsPreferredPaymentMethod(selectedPaymentProduct.kind)) return
      if (!STRIPE_PRIMARY_PRODUCT_KINDS.has(selectedPaymentProduct.kind)) return

      const savedMethods = savedStripePaymentMethodsMap[selectedPaymentProduct.kind]
      if (!savedMethods || savedMethods.length === 0) setSelectedSavedPaymentMethod(null)

    }, [savedStripePaymentMethodsMap, stripePaymentMethods, selectedPaymentProduct])

    return (
      <UpsellPaymentContext.Provider
        value={{
          paymentIntent,
          isStripeUsed,
          paymentProducts,
          selectedPaymentProduct,
          isForeignPurchase,
          isPaymentElementReady,
          isPaymentElementValid,
          isStripeProcessing,
          isStripeConfirmed,
          stripeError,
          isPurchaseReady,
          updatePreferredPaymentMethod,
          receiptEmail,
          isReceiptEmailValid,
          saveStripePaymentMethod,
          selectedSavedPaymentMethod,
          isPaymentLoading,
          doStripeRedirect,
          setIsStripeProcessing,
          setIsStripeConfirmed,
          setStripeError,
          setIsPaymentElementValid,
          setIsPaymentElementReady,
          setSelectedPaymentProduct,
          setReceiptEmail,
          setIsReceiptEmailValid,
          setSaveStripePaymentMethod,
          setSelectedSavedPaymentMethod,
          setDoStripeRedirect,
        }}
      >
        {children}
      </UpsellPaymentContext.Provider>
    )
  }
